/**
 * useToolRecommendations Hook
 * 
 * Recommends tools based on detected input type.
 * Uses both declarative supportedInputTypes and runtime supportsInput() validation.
 */

import { useMemo } from 'react';

import { ToolRegistry } from '@/engine/ToolRegistry';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { ITool } from '@/interfaces/ITool';
import { isGenerator,isTransformer } from '@/interfaces/ITool';

interface UseToolRecommendationsOptions {
  /** Maximum number of recommendations */
  maxResults?: number;
  /** Include generators in recommendations */
  includeGenerators?: boolean;
}

export interface IRecommendation {
  tool: ITool;
  score: number;
  reason: string;
  /** True if tool passed runtime supportsInput() validation */
  validated: boolean;
}

/**
 * Hook to get tool recommendations based on input and detected type.
 * 
 * Uses a hybrid approach for maximum accuracy:
 * 1. Fast filtering via supportedInputTypes (declarative)
 * 2. Accurate validation via supportsInput() (runtime, if available)
 * 
 * @param input - The actual input string (for runtime validation)
 * @param detectedType - The detected semantic type
 * @param options - Configuration options
 */
export function useToolRecommendations(
  input: string,
  detectedType: ISemanticType | null,
  options: UseToolRecommendationsOptions = {}
): IRecommendation[] {
  const { maxResults = 5, includeGenerators = false } = options;

  return useMemo(() => {
    if (!detectedType) {
      // Return popular generators when no input
      if (includeGenerators) {
        return ToolRegistry.getAll()
          .filter(isGenerator)
          .slice(0, maxResults)
          .map(tool => ({
            tool,
            score: 50,
            reason: 'Generator tool',
            validated: false,
          }));
      }
      return [];
    }

    const recommendations: IRecommendation[] = [];
    const tools = ToolRegistry.getAll();

    for (const tool of tools) {
      if (isGenerator(tool) && !includeGenerators) continue;

      if (isTransformer(tool) && tool.supportedInputTypes) {
        // Step 1: Fast check via declared supportedInputTypes
        const supportsType = tool.supportedInputTypes.some(
          supportedType => supportedType.semanticType === detectedType.semanticType
        );

        if (!supportsType) continue;

        // Step 2: Runtime validation via supportsInput() if available
        let validated = false;
        if (tool.supportsInput && input) {
          const actuallySupports = tool.supportsInput(input, detectedType.semanticType);
          if (!actuallySupports) continue; // Skip even if type matched declaratively
          validated = true; // Passed runtime validation
        }

        // Passed both checks - add to recommendations
        const exactMatch = tool.supportedInputTypes.some(
          t => t.semanticType === detectedType.semanticType
        );

        recommendations.push({
          tool,
          score: exactMatch ? 100 : 70,
          reason: exactMatch 
            ? `Works with ${detectedType.semanticType}`
            : `Compatible with ${detectedType.semanticType}`,
          validated,
        });
      }
    }

    // Sort by score and limit results
    return recommendations
      .sort((a, b) => b.score - a.score)
      .slice(0, maxResults);
  }, [input, detectedType, maxResults, includeGenerators]);
}

